import { defineStore } from "pinia";
import { ref, computed } from "vue";
import _ from "lodash";
import { useRealtimeDatabase } from "../composables/useRealtimeDatabase";
import { onValue, onChildAdded, child, set } from "firebase/database";
import { JobchainV1_1 } from "~/class/JobchainV1-1";
import data from '~/samples/jobchainV1/data.js'
import {
  replayJobchain,
  buildTableInstances,
  type BillingContext,
} from "~/utils/jobchainRecompute";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { activeSkipServiceResolver } from "~/utils/billingResolvers";
import { JobAction } from "~/interface/Job";
import { bucket, isOfflineBoot, offlineBucketNames } from "~/services/storage/offlineBuckets";
import {
  loadPerRowBucket,
  mirrorRowToIDB,
  waitForFirstRTDBSettle,
} from "~/services/bootstrap/layerBHelpers";

const LOCAL_STORAGE_PREFIX = "__jobchain:"

// Pending keys carry the customer: `__jobchain:cid-<customerId>:<nodeKey>`. Shops
// share devices and restoreOffline writes into the CURRENT shop's jobchain, so an
// unscoped key let shop A's unsynced job land in shop B after a sign-in switch.
// Keys written before this change (`__jobchain:<nodeKey>`) have no known owner:
// they are never replayed, and only the terminal purge deletes them.
const CUSTOMER_SCOPED_PREFIX = `${LOCAL_STORAGE_PREFIX}cid-`
const pendingPrefixFor = (customerId: string) => `${CUSTOMER_SCOPED_PREFIX}${customerId}:`
const isLegacyPendingKey = (key: string) =>
  key.startsWith(LOCAL_STORAGE_PREFIX) && !key.startsWith(CUSTOMER_SCOPED_PREFIX)

// Resolve the IDB mirror bucket for the current customer. Returns null when
// auth hasn't populated storeId yet (callers should no-op in that case).
function getJobsBucket() {
  const sid = useAuthStore().storeId;
  if (!sid) return null;
  return bucket(offlineBucketNames.jobs(String(sid)));
}

export const usePosJobsStore = defineStore("jobsV1", () => {
  const jobsReady = ref(false);
  const loaded = ref(false)

  const billNumberStore = usePosBillsNumberStore()
  const jobs = ref({});
  const tables = ref<{}>({});
  const _businessDate = ref<string | null>(null)

  // In-memory dedup set — tracks job keys already mirrored to IDB. Populated
  // by restoreFromIDB at boot; checked before every IDB write so subscribed
  // RTDB events (which replay existing children on first subscribe) and
  // repeated local adds don't hammer IDB with redundant writes.
  const knownKeys = new Set<string>()

  const mirrorJobToIDB = (key: string, job: any) => {
    mirrorRowToIDB(getJobsBucket(), knownKeys, key, job)
  }

  window.TABLES = tables;
  window.JOBS = jobs;

  // Wire up cross-store watchers + start RTDB subscription. Idempotent —
  // called from both bootOnline and bootOffline after their respective IDB
  // restore step.
  function wireWatchersAndSubscribe() {
    const tableStateStore = usePosTableStatesStore()
    // These two watchers deliberately call computeJobchainLazy() and NOT
    // markChainDirty(): guests / lock / per-table + shop-wide service charge / VAT
    // / split-bill assignment change how the SAME items are priced. The jobchain
    // is untouched, so they take the billing-only path (~2.6ms at 1,000 jobs
    // instead of ~27.7ms). If a real job lands in the same debounce window its
    // markChainDirty() has already set the flag, so that tick replays in full.
    watch([() => tableStateStore.tablesState, () => tableStateStore.tableBillingGlobal], () => {
      computeJobchainLazy()
    }, { deep: true })
    watch(() => billNumberStore.uuidItemToBillNumber, () => {
      computeJobchainLazy()
    }, { deep: true })
    watchRealtime()
    void restoreOffline()
  }

  /** Online boot: seed knownKeys from IDB, subscribe RTDB, wait for first
   *  settle. Replaces the old 4-second setTimeout — `loaded` now flips on
   *  actual RTDB signal (or short fallback timeout when the jobchain is empty). */
  async function bootOnline() {
    const { knownKeys: seeded } = await loadPerRowBucket<any>(getJobsBucket(), false)
    seeded.forEach((k) => knownKeys.add(k))
    wireWatchersAndSubscribe()
    const jobRef = useRealtimeDatabase().jobchainRef()
    await waitForFirstRTDBSettle(jobRef)
    resolveBusinessDate()
    loaded.value = true
  }

  /** Offline boot: restore everything from IDB into Pinia and mark loaded
   *  immediately. Subscribe RTDB in the background (will replay once
   *  connectivity returns). */
  async function bootOffline() {
    return 
    const { entries, knownKeys: seeded } = await loadPerRowBucket<any>(getJobsBucket(), true)
    seeded.forEach((k) => knownKeys.add(k))
    if (entries.length) {
      const restored: Record<string, any> = {}
      for (const [k, job] of entries) restored[k] = job
      jobs.value = restored
      markChainDirty()
    }
    wireWatchersAndSubscribe()
    resolveBusinessDate()
    loaded.value = true
  }

  // Backward-compat shim. New code should call bootOnline/bootOffline via
  // the bootstrap strategy. Kept for callers outside the bootstrap path.
  async function init() {
    if (isOfflineBoot()) return bootOffline()
    return bootOnline()
  }

  async function debugInitJobs() {
    //  jobs.value = data
    markChainDirty()
    loaded.value = true
  }

  function add(job, sync) {

    let jobRef = useRealtimeDatabase().jobchainRef()
    let key = job.jobId.replaceAll(".", "_")
    const jobChildRef = child(jobRef, key);
    const saveKey = `${pendingPrefixFor(String(useAuthStore().storeId || ""))}${key}`
    saveOffline(saveKey, job)

    // Synchronous optimistic insert — DELIBERATE and load-bearing for Job # dedup.
    // add() is called synchronously inside watchOrderFromTerminal's onChildAdded
    // callback (no await), so this line lands the job in jobs.value BEFORE the next
    // queued terminal_job callback assigns its running number. We do NOT rely on the
    // jobchainRef echo (watchRealtime.onChildAdded) to refresh the count in time: if
    // RTDB queues that local echo as a later task, callbacks B/C/D would run first
    // and read a stale count → duplicate "Job #N". Setting jobs.value ourselves here
    // makes the count deterministic regardless of Firebase's local-event timing.
    // Keyed by the same sanitized jobId the echo uses, so the echo just overwrites
    // idempotently. No computeJobchainLazy() needed — getJobCurrentQueueNumber reads
    // jobs.value directly, and the echo recomputes tables for the UI.
    jobs.value[key] = job

    // Mirror to IDB (offline-first), dedup'd via knownKeys.
    // mirrorJobToIDB(key, job)
    set(jobChildRef, job).then(() => {
      releaseOffline(saveKey, null)
    })
  }

  // Cached result of the last replay. Holding the instance (not just its return
  // value) is what lets the billing-only pass re-apply split-bill numbers.
  let _jobchain: JobchainV1_1 | null = null
  // Set whenever `jobs.value` changed since the last replay. Guarded by a single
  // debounced entry point on purpose: if a new job and a guests edit land in the
  // same 200ms window, one flag decides for both and the full replay wins. Two
  // separate debounced functions would race and could settle on the cheap path.
  let _chainDirty = true

  /** Mark the chain as changed and schedule a recompute. Use this for anything
   *  that mutates `jobs.value`; a pure billing input must call
   *  computeJobchainLazy() directly so it stays on the cheap path. */
  function markChainDirty() {
    _chainDirty = true
    computeJobchainLazy()
  }

  /** Gather the pricing inputs. Cheap — plain store reads, no replay. */
  function buildBillingContext(): BillingContext {
    const { tableBillingGlobal, tablesState } = usePosTableStatesStore()

    // no-discount tag is re-derived from the sub-item id at build time (same
    // pattern as ReceiptOrder / OrderItemHelper) — the tag is static per sub-item
    // and never persisted on the order line. A line carrying "no-discount" is
    // excluded from the mode-2 whole-bill discount (see calculateBilling).
    const productsStore = usePosProductsStore()
    const noDiscountResolver = (subItemId: any) =>
      (productsStore.getPrintersByUuid(String(subItemId)) || [])
        .some((t: any) => String(t).trim().toLowerCase() === 'no-discount')

    // "Product" (VAT-inclusive) classifier — undefined unless the active strategy
    // actually reads `skipService`, so default shops resolve nothing per line and
    // never build the sub-item → type map. See utils/billingResolvers.
    const skipServiceResolver = activeSkipServiceResolver()

    return {
      tableBillingGlobal,
      tablesState,
      togoNoService: useSettingsStore().togoNoService,
      noDiscountResolver,
      skipServiceResolver,
      // แบ่งจ่าย
      uuidItemToBillNumber: billNumberStore.uuidItemToBillNumber || {},
    }
  }

  function computeJobchain() {
    loaded.value = true

    // Replay ONLY when the jobs actually changed. A second cashier's jobs arrive
    // out of chronological order, so when we do replay it is always a full
    // re-sort from the head of the chain — never an incremental append.
    const needsReplay = _chainDirty || !_jobchain
    if (needsReplay) {
      // คำนวณ ITEMS - QTY บนโต๊ะมีอะไรบ้าง
      _jobchain = replayJobchain(jobs.value)
      _chainDirty = false
    }
    console.log(`computeJobchain [${needsReplay ? 'full replay' : 'billing only'}] : ` + dayjs().toISOString())

    // คำนวณ ยอดเงินต่าง  items -> billings
    tables.value = buildTableInstances(_jobchain!, buildBillingContext())
  }

  const computeJobchainLazy = _.debounce(computeJobchain, 200)

  function watchRealtime() {


    //jobs
    let jobRef = useRealtimeDatabase().jobchainRef()
    onChildAdded(jobRef, (snapshot) => {

      jobs.value[snapshot.key] = snapshot.val()
      // Mirror inbound RTDB job into IDB — knownKeys dedups against jobs
      // already restored from IDB (RTDB replays all existing children on
      // first subscribe, so without this every reload would re-write the
      // entire jobchain).
      if (snapshot.key) {
        mirrorJobToIDB(snapshot.key, snapshot.val())
      }
      markChainDirty()
    });


  }


  function saveOffline(key, payload) {
    if (!process.client) return
    window.localStorage.setItem(key, JSON.stringify(payload));
  }

  function releaseOffline(key, payload) {
    if (!process.client) return
    window.localStorage.removeItem(key)
  }

  function getPendingJobs(): Array<{ key: string; nodeKey: string; job: any }> {
    if (!process.client) return []

    const pending: Array<{ key: string; nodeKey: string; job: any }> = []

    // Only the signed-in shop's own pending jobs — never another shop's, never
    // legacy unscoped keys (see CUSTOMER_SCOPED_PREFIX).
    const customerId = useAuthStore().storeId
    if (!customerId) return pending
    const prefix = pendingPrefixFor(String(customerId))

    for (let index = 0; index < window.localStorage.length; index++) {
      const key = window.localStorage.key(index)
      if (!key || !key.startsWith(prefix)) continue

      const stored = window.localStorage.getItem(key)
      if (!stored) continue

      try {
        pending.push({
          key,
          nodeKey: key.slice(prefix.length),
          job: JSON.parse(stored),
        })
      } catch (error) {
        console.error("Failed to parse pending job from local storage:", error)
      }
    }

    return pending
  }

  // Terminals never hold a legitimate pending jobchain write: their orders go
  // through terminal_job, and every Move / Comment / Set Price / Split Item they
  // wrote while the jobchain rule required the cashier claim was rejected and
  // left here. Now that terminals get a token, restoreOffline would replay those
  // days later onto tables that have since changed hands — so drop them once per
  // customer — this shop's scoped keys plus any legacy unscoped keys (the old
  // build wrote those). Cashier / 2nd cashier pending jobs are real offline
  // sales: never touched.
  function purgeTerminalPendingJobsOnce() {
    if (!process.client) return
    if (usePosShopStore().runMode !== "terminal") return
    const customerId = useAuthStore().storeId
    if (!customerId) return

    const flagKey = `nl:terminal:pendingJobsPurged:v1:${customerId}`
    try {
      if (window.localStorage.getItem(flagKey)) return
      const ownPrefix = pendingPrefixFor(String(customerId))
      const stale: string[] = []
      for (let index = 0; index < window.localStorage.length; index++) {
        const key = window.localStorage.key(index)
        if (key && (key.startsWith(ownPrefix) || isLegacyPendingKey(key))) stale.push(key)
      }
      for (const key of stale) window.localStorage.removeItem(key)
      window.localStorage.setItem(flagKey, new Date().toISOString())
      if (stale.length) {
        console.warn(`[jobs] terminal: discarded ${stale.length} stale pending jobchain write(s)`)
      }
    } catch (error) {
      console.warn("[jobs] terminal pending-job purge failed", error)
    }
  }

  async function restoreOffline() {
    if (!process.client) return []

    console.log("dispatch - restoreOffline started")

    purgeTerminalPendingJobsOnce()
    const pendingJobs = getPendingJobs()
    if (!pendingJobs.length) return []

    const jobRef = useRealtimeDatabase().jobchainRef()

    for (const pending of pendingJobs) {
      jobs.value[pending.nodeKey] = pending.job
    }
    markChainDirty()
    resolveBusinessDate()

    await Promise.all(
      pendingJobs.map(async ({ key, nodeKey, job }) => {
        try {
          const jobChildRef = child(jobRef, nodeKey)
          await set(jobChildRef, job)
          releaseOffline(key, null)
        } catch (error) {
          console.error("Failed to restore offline job:", nodeKey, error)
        }
      })
    )

    return pendingJobs
  }

  async function syncPendingJobs() {
    await restoreOffline()
    return getPendingJobs()
  }

  const getJobsArray = () => {
    return Object.values(jobs.value) as any[]
  }

  const getBusinessDateFromTimestamp = (timestamp?: string | number | Date | null, format = 'YYYY-MM-DD') => {
    const target = timestamp ? dayjs(timestamp) : dayjs()
    const businessMode = useSettingsStore().getSystemEnvByName("BusinessMode")?.Value || "0"

    if (businessMode == 1) {
      return target.add(-7, 'hour').format(format)
    }

    return target.format(format)
  }

  const getLatestOpenShopJob = () => {
    return getJobsArray()
      .filter((job) => job?.type === JobAction.OPEN_SHOP)
      .sort((a, b) => dayjs(b.timestamp).diff(dayjs(a.timestamp)))[0] || null
  }

  // The business date this open-shop session was declared for. Prefer the explicit
  // `businessDate` chosen at open time (lets a late-night 24h venue declare the
  // session as the NEW business day even when opened before the 07:00 cutoff);
  // fall back to computing from the timestamp for older jobs that lack the field.
  const getOpenShopBusinessDate = (openShopJob) => {
    if (!openShopJob) return null
    return openShopJob.businessDate || getBusinessDateFromTimestamp(openShopJob.timestamp)
  }

  const isRunningOverClosedDay = () => {
    const latestOpenShopJob = getLatestOpenShopJob()
    if (!latestOpenShopJob?.timestamp) {
      return false
    }

    const openShopBusinessDate = getOpenShopBusinessDate(latestOpenShopJob)
    const currentBusinessDate = getBusinessDateFromTimestamp()

    // Block only when the current business day is strictly PAST the declared one.
    // (current < declared can happen right after a "open for the new day" choice
    // near the cutoff — that must NOT block.)
    return dayjs(currentBusinessDate).isAfter(dayjs(openShopBusinessDate), 'day')
  }

  // "6h 30m ago" style elapsed-since string. Days roll up past 24h.
  const formatElapsed = (timestamp: string | number | Date) => {
    const diffMin = Math.max(0, dayjs().diff(dayjs(timestamp), 'minute'))
    const d = Math.floor(diffMin / (60 * 24))
    const h = Math.floor((diffMin % (60 * 24)) / 60)
    const m = diffMin % 60
    const parts: string[] = []
    if (d) parts.push(`${d}d`)
    if (h) parts.push(`${h}h`)
    if (m || parts.length === 0) parts.push(`${m}m`)
    return `${parts.join(' ')} ago`
  }

  /**
   * Single source of truth for the "shop running over closed day" warning.
   * Shows when the shop was opened (date + time) and the business-date mismatch
   * so the cashier understands why new orders are blocked. Used by both the POS
   * landing flow (pages/v2/pos.vue) and the terminal Add guard
   * (pos.tableInstance.ts → addRequest) so the message + data stay identical.
   */
  const showRunningOverClosedDayAlert = () => {
    const openShopJob = getLatestOpenShopJob()
    const openedAt = openShopJob?.timestamp
      ? dayjs(openShopJob.timestamp).format('D MMM YYYY HH:mm')
      : null
    const openedAgo = openShopJob?.timestamp
      ? formatElapsed(openShopJob.timestamp)
      : null
    const openShopBusinessDate = openShopJob?.timestamp
      ? getOpenShopBusinessDate(openShopJob)
      : null
    const currentBusinessDate = getBusinessDate()

    const openedSuffix = openedAgo ? ` <span style="color:#888;">(${openedAgo})</span>` : ''
    const openedLine = openedAt
      ? `<div style="margin-top:12px;line-height:1.7;font-size:14px;">
           <div>🟢 เปิดร้านเมื่อ: <b>${openedAt} น.</b>${openedSuffix}</div>
           <div>📅 รอบ business date ที่เปิด: <b>${openShopBusinessDate}</b></div>
           <div>📅 business date วันนี้: <b>${currentBusinessDate}</b></div>
         </div>`
      : ''

    return Swal.fire({
      icon: 'warning',
      title: 'รอบเปิดร้านคนละวัน / Shop running over closed day',
      html: `ตรวจพบว่ารอบเปิดร้านปัจจุบันไม่ตรงกับ business date ของวันนี้ กรุณาปิดร้านก่อนแล้วเปิดร้านใหม่${openedLine}`,
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    })
  }

  // คำนวน business date, ONCE
  function resolveBusinessDate() {
    if (_businessDate.value) return

    const jobsArray = getJobsArray()
    if (jobsArray.length === 0) {
      _businessDate.value = dayjs().toISOString()
      return
    }

    jobsArray.sort((a, b) => dayjs(a.timestamp).diff(dayjs(b.timestamp)))

    const jobOpenTime = getLatestOpenShopJob()
    _businessDate.value = jobOpenTime ? jobOpenTime.timestamp : jobsArray[0].timestamp
  }

  const getBusinessDate = (format = 'YYYY-MM-DD') => {
    return getBusinessDateFromTimestamp(undefined, format)
  };

  const getFirstJob = () => {
    const jobsArray = getJobsArray()
    if (jobsArray.length === 0) {
      return null
    }
    jobsArray.sort((a, b) => dayjs(a.timestamp).diff(dayjs(b.timestamp)))
    return jobsArray[0]
  }



  const getLastUpdate = () => {
    const jobsArray = getJobsArray();
    if (jobsArray.length === 0) {
      return null;
    }

    const lastJob = jobsArray.reduce((acc: any, job: any) => {
      if (!acc) return job;
      return job.timestamp > acc.timestamp ? job : acc;
    }, null);

    return lastJob?.timestamp || null;

  }

  // Shift number is derived from the job chain, NOT a separate counter: count the
  // CHANGE_SHIFT (type 110) jobs and add 1. No jobs → Shift 1. It is intentionally
  // NOT anchored to OPEN_SHOP and does NOT reset on Open Shop (a power-cycle + Open
  // Shop must not reset the shift). CLOSE_SHOP clears the chain, so the next business
  // day naturally starts back at Shift 1.
  const getShiftNumber = () => {
    return 1 + getJobsArray().filter((job) => job?.type === JobAction.CHANGE_SHIFT).length
  }

  const getShift = () => {
    return `Shift ${getShiftNumber()}`
  }

  return {
    name: "jobs",
    init,
    bootOnline,
    bootOffline,
    tables,
    jobs,
    add,
    loaded,
    getBusinessDate,
    getBusinessDateFromTimestamp,
    getLastUpdate,
    getShift,
    getShiftNumber,
    resolveBusinessDate,
    getFirstJob,
    getJobsArray,
    getLatestOpenShopJob,
    isRunningOverClosedDay,
    showRunningOverClosedDayAlert,
    restoreOffline,
    getPendingJobs,
    syncPendingJobs,

  };
});
